import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Tabs,
    Tab,
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { alpha, useTheme } from '@mui/material/styles';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { addTask, editTask, setTasks, deleteTask, setParents } from '../redux/taskSlice';
import ParentManager from './ParentManager';
import TaskModal from './TaskModal';

import { API_URL, apiFetch } from "../config";
const TASK_STATUSES = ["toDo", "inProgress", "onHold", "complete"]; // Predefined task statuses

const DARK_COLORS = [
    "#1E88E5", // Blue
    "#43A047", // Green
    "#F4511E", // Deep Orange
    "#6A1B9A", // Purple
    "#00897B", // Teal
    "#D32F2F", // Red
    "#C0CA33", // Lime
    "#5E35B1", // Indigo
    "#3949AB", // Dark Blue
    "#FF6F00", // Amber
];

// Assign a unique color to each task based on its ID or title.
const getTaskColor = (taskId) => DARK_COLORS[taskId % DARK_COLORS.length];

const Tasks = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0); // 0 = Board, 1 = Calendar
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null); // Task for the modal
    const [customers, setCustomers] = useState([]);
    const [openParentManager, setOpenParentManager] = useState(false);
    const [taskForm, setTaskForm] = useState({
        id: '',
        title: '',
        description: '',
        priority: 'Low',
        parent: 'General',
        startDate: '',
        deadlineDate: '',
        customerId: '',
    });
    // Add this state for feedback
    const [feedback, setFeedback] = useState({ message: '', severity: '', open: false });

    const tasks = useSelector((state) => state.tasks.tasks);
    const parents = useSelector((state) => state.tasks.parents);
    const dispatch = useDispatch();

    const handleTabChange = (_, newValue) => setActiveTab(newValue);
    const fetchTasks = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/tasks`);
            const data = await response.json();
            if (response.ok) {
                const groupedTasks = data.reduce((acc, task) => {
                    if (!acc[task.status]) acc[task.status] = [];
                    acc[task.status].push(task);
                    return acc;
                }, {});

                TASK_STATUSES.forEach((status) => {
                    if (!groupedTasks[status]) groupedTasks[status] = [];
                });

                dispatch(setTasks(groupedTasks));
            } else {
                console.error("Failed to fetch tasks:", data.error);
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    }, [dispatch]);
    const fetchCustomers = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/customers`);
            const data = await response.json();
            setCustomers(data);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    }, []);

    const fetchParents = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/tasks/parents`);
            const data = await response.json();
            if (response.ok) {
                dispatch(setParents(data.map((parent) => parent.name)));
            } else {
                console.error("Failed to fetch parents:", data.error);
            }
        } catch (error) {
            console.error("Error fetching parents:", error);
        }
    }, [dispatch]);


    const handleAddTask = async () => {
        if (!taskForm.title || !taskForm.priority || !taskForm.startDate || !taskForm.deadlineDate) {
            setFeedback({ message: 'All fields are required.', severity: 'warning', open: true });
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/tasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...taskForm, status: 'toDo' }),
            });
            if (response.ok) {
                const data = await response.json();
                dispatch(addTask(data));
                setOpenDialog(false);
                setFeedback({ message: 'Task added successfully!', severity: 'success', open: true });
            } else {
                throw new Error('Failed to add task.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };

    const handleSaveTask = async (updatedTask) => {
        try {
            const response = await apiFetch(`${API_URL}/tasks/${updatedTask.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedTask),
            });
            if (response.ok) {
                dispatch(editTask(updatedTask));
                setFeedback({ message: 'Task updated successfully!', severity: 'success', open: true });
            } else {
                throw new Error('Failed to update task.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };

    const onDragEnd = async (result) => {
        const { source, destination } = result;

        if (!destination) return;

        const sourceCategory = source.droppableId;
        const destinationCategory = destination.droppableId;

        if (sourceCategory === destinationCategory) {
            const reorderedTasks = [...tasks[sourceCategory]]; // Create a shallow copy
            const [movedTask] = reorderedTasks.splice(source.index, 1);
            reorderedTasks.splice(destination.index, 0, movedTask);

            dispatch(setTasks({
                ...tasks,
                [sourceCategory]: reorderedTasks,
            }));

            try {
                await apiFetch(`${API_URL}/tasks/reorder`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ status: sourceCategory, tasks: reorderedTasks }),
                });
            } catch (err) {
                console.error("Error saving task order:", err);
            }
            return;
        }

        const sourceTasks = [...tasks[sourceCategory]]; // Shallow copy
        const destinationTasks = [...tasks[destinationCategory]]; // Shallow copy
        const movedTask = { ...sourceTasks[source.index], status: destinationCategory }; // Copy and update status

        sourceTasks.splice(source.index, 1);
        destinationTasks.splice(destination.index, 0, movedTask);

        dispatch(setTasks({
            ...tasks,
            [sourceCategory]: sourceTasks,
            [destinationCategory]: destinationTasks,
        }));

        try {
            await apiFetch(`${API_URL}/tasks/${movedTask.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(movedTask),
            });

            await apiFetch(`${API_URL}/tasks/reorder`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: destinationCategory, tasks: destinationTasks }),
            });
        } catch (err) {
            console.error("Error saving task changes:", err);
        }
    };

    const handleDeleteTask = async (taskId) => {
        try {
            const response = await apiFetch(`${API_URL}/tasks/${taskId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                dispatch(deleteTask(taskId));
                setFeedback({ message: 'Task deleted successfully!', severity: 'success', open: true });
            } else {
                throw new Error('Failed to delete task.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };



    useEffect(() => {
        fetchTasks();
        fetchParents();
        fetchCustomers();
    }, [fetchTasks, fetchParents, fetchCustomers]);

    return (
        <Box p={1} sx={{ backgroundColor: 'background.default', color: 'text.primary' }}>
            <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
                <Tab label="Board" />
                <Tab label="Calendar" />
            </Tabs>

            {/* Add Task Button */}

            {activeTab === 0 && (
                <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={1} // Add spacing between the buttons
                    mb={2} // Add spacing below the button section
                    mt={2} // Add spacing above the button section
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setOpenDialog(true)}
                    >
                        Add Task
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setOpenParentManager(true)}
                    >
                        Manage Task Parents
                    </Button>
                </Box>
            )}


            {activeTab === 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Grid container spacing={2}>
                        {TASK_STATUSES.map((status) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={status}>
                                <Card sx={{ backgroundColor: "background.paper", borderRadius: 2 }}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.2rem',
                                                color: 'text.primary',
                                                fontWeight: 'bold',
                                                mb: 2,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {status === 'toDo'
                                                ? 'To Do'
                                                : status === 'onHold'
                                                    ? 'On Hold'
                                                    : status === 'inProgress'
                                                        ? 'In Progress'
                                                        : 'Complete'}
                                        </Typography>
                                        <Droppable droppableId={status}>
                                            {(provided) => (
                                                <Box
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    sx={{
                                                        minHeight: '120px',
                                                        padding: 1,
                                                        backgroundColor: 'transparent',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    {tasks[status]?.map((task, index) => (
                                                        <Draggable
                                                            key={String(task.id)}
                                                            draggableId={String(task.id)}
                                                            index={index}
                                                        >
                                                            {(provided) => (
                                                                <Card
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    sx={{
                                                                        marginBottom: 1,
                                                                        padding: 1,
                                                                        backgroundColor: 'background.default',
                                                                        color: 'text.primary',
                                                                        boxShadow: 1,
                                                                        borderRadius: 1,
                                                                        transition: 'box-shadow 0.3s ease-in-out',
                                                                        '&:hover': {
                                                                            boxShadow: (theme) =>
                                                                                `0 0 0 2px ${alpha(theme.palette.primary.main, 0.45)}`,
                                                                        },
                                                                    }}
                                                                    onClick={() => setSelectedTask(task)}
                                                                >
                                                                    <CardContent sx={{ padding: '8px' }}>
                                                                        <Typography
                                                                            variant="subtitle1"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                fontSize: '1rem',
                                                                                marginBottom: 0.5,
                                                                                color: 'text.primary',
                                                                            }}
                                                                        >
                                                                            {task.title}
                                                                        </Typography>
                                                                        {/* <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '0.875rem',
                                                                                color: getPriorityColor(task.priority),
                                                                            }}
                                                                        >
                                                                            {task.priority} Priority
                                                                        </Typography> */}

                                                                        {task.parent && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="primary.main"
                                                                                sx={{ fontSize: '0.8rem' }}
                                                                            >
                                                                                Parent: {task.parent}
                                                                            </Typography>
                                                                        )}
                                                                        {task.customerId && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{ fontSize: '0.8rem' }}
                                                                            >
                                                                                Customer: {customers.find((c) => c.id === task.customerId)?.name || 'N/A'}
                                                                            </Typography>
                                                                        )}
                                                                    </CardContent>
                                                                </Card>
                                                            )}
                                                        </Draggable>

                                                    ))}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </DragDropContext>

            )}


            {activeTab === 1 && (
                <Box
                    mt={3}
                    sx={{
                        backgroundColor: "background.paper",
                        p: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        "& .fc": { color: "text.primary" },
                        "& .fc-col-header-cell, & .fc-daygrid-day-number, & .fc-toolbar-title": {
                            color: "text.primary",
                        },
                        "& .fc-button": {
                            backgroundColor: `${theme.palette.primary.main} !important`,
                            borderColor: `${theme.palette.primary.main} !important`,
                            color: `${theme.palette.primary.contrastText} !important`,
                            "&:hover": {
                                backgroundColor: `${theme.palette.primary.dark} !important`,
                                borderColor: `${theme.palette.primary.dark} !important`,
                            },
                        },
                        "& .fc-button-active": {
                            backgroundColor: `${theme.palette.primary.dark} !important`,
                            borderColor: `${theme.palette.primary.dark} !important`,
                        },
                    }}
                >
                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        events={Object.values(tasks)
                            .flat()
                            .map((task, index) => ({
                                title: task.title,
                                start: task.startDate,
                                end: task.deadlineDate,
                                backgroundColor: getTaskColor(index), // Assign unique color
                                borderColor: getTaskColor(index), // Border matches background
                            }))}
                        editable
                        selectable
                        eventClick={(info) => {
                            const clickedTask = Object.values(tasks)
                                .flat()
                                .find((task) => task.title === info.event.title);
                            if (clickedTask) setSelectedTask(clickedTask);
                        }}
                        eventDisplay="block" // Ensures colored background is fully visible
                    />
                </Box>
            )}




            {/* Task Modal */}
            {selectedTask && (
                <TaskModal
                    task={selectedTask}
                    parents={parents}
                    customers={customers} // Pass customers to the TaskModal
                    open={!!selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onSave={handleSaveTask}
                    onDelete={handleDeleteTask}
                />
            )}



            {/* Add Task Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Add Task</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Title"
                        fullWidth
                        margin="normal"
                        value={taskForm.title}
                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                        value={taskForm.description}
                        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    />
                    <TextField
                        label="Priority"
                        fullWidth
                        margin="normal"
                        select
                        value={taskForm.priority}
                        onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                        SelectProps={{
                            native: true,
                        }}
                    >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Immediate">Immediate</option>
                    </TextField>
                    <TextField
                        label="Parent"
                        fullWidth
                        margin="normal"
                        select
                        value={taskForm.parent}
                        onChange={(e) => setTaskForm({ ...taskForm, parent: e.target.value })}
                        SelectProps={{
                            native: true,
                        }}
                    >
                        {parents.map((parent) => (
                            <option key={parent} value={parent}>
                                {parent}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        label="Starting Date"
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={taskForm.startDate}
                        onChange={(e) => setTaskForm({ ...taskForm, startDate: e.target.value })}
                    />
                    <TextField
                        label="Deadline Date"
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={taskForm.deadlineDate}
                        onChange={(e) => setTaskForm({ ...taskForm, deadlineDate: e.target.value })}
                    />
                    <TextField
                        label="Customer"
                        fullWidth
                        margin="normal"
                        select
                        value={taskForm.customerId}
                        onChange={(e) => setTaskForm({ ...taskForm, customerId: e.target.value })}
                        SelectProps={{
                            native: true,
                        }}
                    >
                        <option value="">None</option>
                        {customers.map((customer) => (
                            <option key={customer.id} value={customer.id}>
                                {customer.name}
                            </option>
                        ))}
                    </TextField>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddTask} variant="contained" color="primary">
                        Add Task
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Parent Manager Dialog */}
            <Dialog open={openParentManager} onClose={() => setOpenParentManager(false)}>
                <DialogTitle>Manage Parent Categories</DialogTitle>
                <DialogContent>
                    <ParentManager />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenParentManager(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={() => setFeedback({ ...feedback, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={feedback.severity}>{feedback.message}</Alert>
            </Snackbar>

        </Box>
    );
};

export default Tasks;
